import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';

import { useTrackLinks } from '#/hooks/useTrackLinks';
import { textStyles } from '#/theme/theme';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';

import { StyledText } from './Text.styled';
import { useLinksHaveUTMParams } from './useLinksHaveUTMParams';

type Props = {
  id?: string;
  className?: string;
  tag?: string;
  htmlFor?: string;
  styleAs?: keyof typeof textStyles;
  children?: ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  analyticsData?: {
    eventName: string;
    eventProperties?: Record<string, unknown>;
  };
  title?: string;
  qaId?: string;
  lineClamp?: number;
  style?: CSSProperties;
};

export function Text({
  id,
  className,
  tag = 'div',
  htmlFor,
  styleAs,
  children,
  qaId,
  analyticsData,
  dangerouslySetInnerHTML,
  title,
  lineClamp,
  style,
  ...otherProps
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useTrackLinks({
    ref,
    eventName: analyticsData?.eventName,
    eventProperties: analyticsData?.eventProperties,
  });

  useLinksHaveUTMParams({ ref });

  const additionalProps = { ...getDataAndAriaProps(otherProps) };
  if (htmlFor) additionalProps['htmlFor'] = htmlFor;

  return (
    <StyledText
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...additionalProps}
      as={tag}
      ref={ref}
      id={id}
      className={className}
      data-qa-id={qaId}
      title={title}
      $styleAs={styleAs}
      $lineClamp={lineClamp}
      style={style}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </StyledText>
  );
}
